import { FC, useCallback, useContext } from 'react';

import { Button, Separator, BucketIcon, AmountBox } from '@smartshell/ui-kit';

import { useIsCashboxEnabled } from 'cashbox';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { TShopItem } from 'types';
import { calcLimit, getItemTotalPrice } from 'utils';

import { IShoppingCartProps } from './interfaces';
import s from './ShoppingCart.module.scss';
import { getTariffDuration, isLastTariff } from './utils';

export const ShoppingCart: FC<IShoppingCartProps> = ({
  combo,
  setCombo,
  items,
  discount,
  setItemAmount,
  removeItem,
  emptyPlaceholder,
  availableItems,
}) => {
  const { t } = useContext(LocalizationContext);
  const isCashboxEnabled = useIsCashboxEnabled();
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const defaultPlaceholder = <span>{t('shoppingCart.empty')}</span>;

  let comboRendered = false;

  function formatDuration(durationInSeconds: number): string {
    const hours = durationInSeconds / 3600;
    return Number.isInteger(hours) ? String(hours) : hours.toFixed(2);
  }
  const isItemApplicableForDiscount = useCallback(
    (item: TShopItem): boolean => {
      if (!discount) return false;

      if (availableItems?.length) {
        return availableItems.some(
          (availableItem) =>
            availableItem.entity_id === item.id &&
            String(availableItem.entity_type) === String(item.type)
        );
      }

      return item.use_global_discounts === true;
    },
    [discount, availableItems]
  );

  return (
    <div className={s.root} data-testid="shopping-cart">
      {items.length > 0 ? (
        items.map((item, index) => {
          if (item.comboId) {
            if (comboRendered) return null;
            if (!combo) return null;

            comboRendered = true;

            const comboForPrice = {
              use_global_discounts: false,
              amount: 1,
              cost: combo.cost ?? 0,
            };

            const purePrice = getItemTotalPrice(comboForPrice);
            const priceWithDiscount = getItemTotalPrice(
              comboForPrice,
              discount
            );

            return (
              <div
                key={`shopping-cart-${combo.title}-${combo.id}-${index}`}
                className={s.item}
              >
                <div className={s.top}>
                  <div className={s.info}>{combo.title}</div>
                  {(item.type !== 'TARIFF' || isLastTariff(items, index)) && (
                    <Button
                      variant="ghost"
                      onClick={() => setCombo(null)}
                      dataTestId="remove"
                    >
                      <BucketIcon color="secondary" size="m" />
                    </Button>
                  )}
                </div>
                <div className={s.contents}>
                  {combo.items?.map((item) => (
                    <div key={item.entity.id} className={s.tooltip_block}>
                      <span className={s.title}>{item.entity.title}</span>
                      <span className={s.count}>
                        {item.amount} {t('pcs')}.
                      </span>
                    </div>
                  ))}
                  {combo.tariff && (
                    <div className={s.tooltip_block}>
                      <span className={s.title}>{combo.tariff.title}</span>
                      <span className={s.count}>
                        {formatDuration(combo.tariff.duration)} {t('hour')}
                      </span>
                    </div>
                  )}
                </div>
                <Separator />
                <div className={s.prices}>
                  <span>
                    {priceWithDiscount.toLocaleString()} {currency.sign}
                  </span>
                  {discount && purePrice !== priceWithDiscount && (
                    <span className={s['base-price']}>
                      {purePrice.toLocaleString()} {currency.sign}
                    </span>
                  )}
                </div>
              </div>
            );
          }

          const purePrice = getItemTotalPrice(item);
          const isApplicable = isItemApplicableForDiscount(item);
          const shouldApplyDiscount = discount && isApplicable;
          const priceWithDiscount = shouldApplyDiscount
            ? getItemTotalPrice(item, discount)
            : purePrice;

          return (
            <div
              key={`shopping-cart-${item.type}-${item.id}-${index}`}
              className={s.item}
              data-testid="item"
            >
              <div className={s.top}>
                <div className={s.info}>
                  {item.original.title}
                  {item.type === 'TARIFF' && item.original && (
                    <span className={s.duration}>
                      {getTariffDuration(item.original, t)}
                    </span>
                  )}
                </div>
                {(item.type !== 'TARIFF' || isLastTariff(items, index)) && (
                  <Button
                    variant="ghost"
                    onClick={() => removeItem(index)}
                    dataTestId="remove"
                  >
                    <BucketIcon color="secondary" size="m" />
                  </Button>
                )}
              </div>
              <div className={s.bottom}>
                {((item.type === 'GOOD' &&
                  (!isCashboxEnabled || !item.original.use_fair_sign)) ||
                  item.type === 'SERVICE') && (
                  <AmountBox
                    nullable
                    limit={calcLimit(item)}
                    value={item.amount}
                    onChange={(amount) =>
                      setItemAmount(item.id, item.type, amount)
                    }
                  />
                )}
                <div className={s.prices}>
                  <span>
                    {priceWithDiscount.toLocaleString()} {currency.sign}
                  </span>
                  {shouldApplyDiscount && purePrice !== priceWithDiscount && (
                    <span className={s['base-price']}>
                      {purePrice.toLocaleString()} {currency.sign}
                    </span>
                  )}
                </div>
              </div>
              <Separator />
            </div>
          );
        })
      ) : (
        <span className={s['empty-placeholder']}>
          {emptyPlaceholder ?? defaultPlaceholder}
        </span>
      )}
    </div>
  );
};
